// src/lib/nudges.js
//
// Motor de nudges. Catálogo declarativo + funciones puras de evaluación.
//
// Una sola fuente de verdad sobre qué mostrar al usuario, en qué orden, según
// su estado. El motor NO consulta Supabase — el hook useOnboarding compone el
// state (campos persistidos + campos derivados en runtime) y lo pasa a estas
// funciones.
//
// Tres superficies de despliegue:
//   - scope 'tour'  → primer viaje (FirstJourney overlay)
//   - scope 'home'  → tarjetas NudgeCard en el Home
//   - scope futuros → calendario, módulos clínicos (agregar entrada nueva)
//
// Forma del state (lo arma useOnboarding):
//   {
//     tour_completado, tour_saltado,
//     perfil_completo, rotativa_cargada, primer_turno_preparado,
//     proximo_turno_at, ultimo_turno_at,
//     ultimo_turno_cerrado, primera_bitacora_escrita,
//     dismissed_nudges,                       // { [nudge_id]: ISO timestamp }
//     tour_force_paso,                        // 1|2|3|null — override post-reset
//     now,                                    // new Date()
//   }
//
// Sobre tour_force_paso:
//   - null (default): el motor deriva el paso del estado real
//     (perfil_completo, rotativa_cargada, primer_turno_preparado). Es el
//     flujo de un signup nuevo.
//   - 1|2|3: el motor muestra el paso correspondiente IGNORANDO las
//     condiciones derivadas. Lo setea useOnboarding.resetTour para que un
//     usuario que ya completó perfil/rotativa pueda re-ver el walkthrough
//     desde paso 1. advanceTourStep lo incrementa.

import { esElegibleTurno } from './perfil';

export const NUDGES = [
  // ── PRIMER VIAJE — sugerencias suaves en el Home (scope='home') ────────────
  // Reemplazan el antiguo tour de overlay oscuro de 3 pasos. Ahora son tarjetas
  // NudgeCard no bloqueantes, con "Ahora no", que orientan sin empujar. El video
  // de onboarding ya muestra el valor; estas solo invitan a configurar.
  {
    id: 'primer_viaje_perfil',
    scope: 'home',
    mode: 'card',
    priority: 1,
    condition: (s) => !s.perfil_completo,
    copy: {
      tag: 'BIENVENIDA/O',
      titulo: 'Cuéntame un poco de ti',
      descripcion: 'Con tu rol y tu etapa, MiPAE se acomoda a tu momento. Son 30 segundos y lo puedes cambiar cuando quieras.',
      cta_label: 'Personalizar MiPAE',
      dismiss_label: 'Más tarde',
    },
    action: { type: 'goto', appState: 'perfil' },
    dismissible: true,
    cooldown_hours: 24,
  },
  {
    id: 'primer_viaje_rotativa',
    scope: 'home',
    mode: 'card',
    priority: 2,
    condition: (s) => s.perfil_completo && !s.rotativa_cargada,
    copy: {
      tag: 'TU CALENDARIO',
      titulo: '¿Armamos tu calendario de turnos?',
      descripcion: 'Cuéntame tu rotación de turnos y te aviso a tiempo qué preparar antes de cada turno. Tú te enfocas en lo clínico, del resto me encargo yo.',
      cta_label: 'Armar mi calendario',
      dismiss_label: 'Ahora no',
    },
    action: { type: 'goto', appState: 'calendario-config' },
    dismissible: true,
    cooldown_hours: 24,
    // Solo a quien el Home le muestra la semana de turnos (el MISMO predicado,
    // esElegibleTurno): al estudiante sin internado en curso se le ofrecía
    // "armar su calendario de turnos" que después ninguna pantalla le muestra
    // (A11, 4-oct-2026). Lo aplica `nudgesDelPerfil`.
    paraPerfil: esElegibleTurno,
  },
  // `primer_viaje_prepara` y `turno_proximo_sin_preparar` (ambos empujaban a
  // "Prepara tu turno") se RETIRARON el 2026-09-14 a pedido del fundador ("ese
  // widget me estresa"): el módulo es material de formación que el menú y el
  // buscador ya no le muestran a la capa de ejercicio, y una tarjeta que
  // vuelve cada 6 horas la víspera del turno es presión, no acompañamiento
  // (anti-culpa: la app documenta, no persigue). No restaurarlos.

  // ── HOME — nudges contextuales (scope='home', mode='card') ─────────────────
  // El aviso 'turno_pasado_sin_cerrar' ("Tu turno de ayer quedó abierto ·
  // PENDIENTE · Cierra el día con una Bitácora") se RETIRÓ el 2026-09-24 por
  // decisión del fundador: desde que la Bitácora es autocuidado (procesar el
  // turno, para todo perfil) no se empuja como tarea. La app documenta, no
  // acusa. No restaurarlo.
];

// ── Motor ────────────────────────────────────────────────────────────────────

/**
 * Deja fuera los nudges que no le corresponden a este perfil (campo opcional
 * `paraPerfil` del catálogo). El estado del motor no sabe del rol ni de la
 * etapa, así que la regla de perfil se aplica sobre la lista ya activa, en el
 * mismo lugar donde App arma los nudges del Home. Sin `paraPerfil` el nudge
 * pasa igual que antes.
 * @param {Array} nudges
 * @param {object|null} profile
 * @returns {Array}
 */
export function nudgesDelPerfil(nudges, profile) {
  return (nudges || []).filter((n) => typeof n.paraPerfil !== 'function' || n.paraPerfil(profile));
}

/**
 * Devuelve nudges activos para un scope, ordenados por prioridad ascendente.
 * Filtra los descartados que aún están dentro de su cooldown.
 */
export function getActiveNudges(scope, state) {
  return NUDGES
    .filter(n => n.scope === scope)
    .filter(n => n.condition(state))
    .filter(n => !isDismissed(n, state.dismissed_nudges, state.now))
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Devuelve el paso de tour activo (solo uno a la vez).
 * Null si el usuario ya completó o saltó el tour.
 */
export function getActiveTourStep(state) {
  if (state.tour_completado || state.tour_saltado) return null;
  const active = getActiveNudges('tour', state);
  return active.length > 0 ? active[0] : null;
}

/**
 * Calcula estado de cada fase del viaje vertical del Home.
 * Devuelve: 'done' | 'active' | 'attention' | 'locked'
 */
export function getViajeFases(state) {
  return {
    antes: (state.perfil_completo && state.rotativa_cargada) ? 'done' : 'active',
    durante: state.rotativa_cargada ? 'active' : 'locked',
    despues: (state.ultimo_turno_at && !state.ultimo_turno_cerrado) ? 'attention'
           : (state.primera_bitacora_escrita ? 'done' : 'locked'),
    fin: 'locked',
  };
}

/**
 * Internal: nudge.dismissible && timestamp guardado && cooldown vigente.
 */
export function isDismissed(nudge, dismissed_nudges, now) {
  if (!nudge.dismissible) return false;
  const ts = dismissed_nudges?.[nudge.id];
  if (!ts) return false;
  const horasDesde = (now - new Date(ts)) / 3.6e6;
  return horasDesde < (nudge.cooldown_hours ?? 24);
}
